import { useState, useMemo } from 'react';
import { PhotoCollection2 } from '@/components/PhotoCollection2';
import { SelectionControls } from '@/components/composed/SelectionControls';
import { usePhotosGraphQL } from '@/graphql/hooks';
import { useDeletePhotoGraphQL } from '@/hooks/graphql/usePhotoMutationsGraphQL';
import { useBatchUploadStandalone } from '@/hooks/useBatchUploadStandalone';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useAuth } from '@/services/auth';
import { PageHeader, PageHeaderLabel, PageHeaderCommandWell } from '@/components/PageHeader';
import { PhotoUploadButton } from '@/components/photos/PhotoUploadButton';
import { FileDropZone } from '@/components/photos/FileDropZone';
import { BatchUploadProgress } from '@/components/photos/BatchUploadProgress';
import { PhotoIcon } from '@/components/icons';
import type { PhotoItem2ViewModel } from '@/components/PhotoItem2/PhotoItem2.types';
import logger from '@/utils/logger';
import { useApolloClient } from '@apollo/client';

export interface MyPhotosProps {
  className?: string;
}

export function MyPhotos({ className = '' }: MyPhotosProps) {
  const { user } = useAuth();
  const { photos, hasMore, loading, loadMore, refetch } = usePhotosGraphQL();
  const { deletePhoto, loading: isDeleting } = useDeletePhotoGraphQL();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const apolloClient = useApolloClient();

  // Infinite scroll
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: loadMore,
    hasMore,
    loading,
  });

  // Batch upload state
  const { state: batchState, addFiles, retryUpload, removeFile, clearCompleted } = useBatchUploadStandalone(
    () => {
      logger.info('MyPhotos', 'Batch upload completed, refetching photos');
      refetch();
    }
  );

  // Map Photo[] to PhotoItem2ViewModel[] to satisfy PhotoCollection2 type requirements
  // Memoized to prevent re-computation on every render
  // IMPORTANT: Must be called before early return to satisfy Rules of Hooks
  const photosData: PhotoItem2ViewModel[] = useMemo(() =>
    (photos || []).map((photo) => ({
      photoData: photo, // Wrap the Photo object
      aspectRatio: photo.width && photo.height ? photo.width / photo.height : undefined,
      critiqueCount: 0, // My Photos doesn't show critique counts
      // Note: textContent omitted - not applicable for My Photos view
    })),
    [photos]
  );

  // Handle invalid file notifications
  const handleInvalidFiles = (summary: string) => {
    logger.warn('MyPhotos', 'Invalid files dropped:', summary);
  };

  const handleSelectionChange = (newSelectedIds: string[]) => {
    setSelectedIds(newSelectedIds);
  };

  const handleSelectAll = () => {
    const allPhotoIds = (photos || []).map((photo) => photo.globalId!);
    setSelectedIds(allPhotoIds);
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedIds.length} photo${selectedIds.length > 1 ? 's' : ''}?`
    );

    if (!confirmed) return;

    // Delete photos in parallel using Promise.allSettled
    const deletePromises = selectedIds.map((photoId) =>
      deletePhoto(photoId)
        .then(() => ({ status: 'fulfilled' as const, photoId }))
        .catch((error) => ({ status: 'rejected' as const, photoId, error }))
    );

    const results = await Promise.all(deletePromises);

    // Track failed deletions
    const failed = results
      .filter((result) => result.status === 'rejected')
      .map((result) => result.photoId);

    if (failed.length > 0) {
      logger.error('MyPhotos', 'Failed to delete photos:', failed);
      // TODO: Show toast notification with failed photo IDs (tracked in GitHub issue)
      // See: https://github.com/codemonkeychris/206mp/issues/137
    }

    // Clear selection after deletions (successful or not)
    setSelectedIds([]);

    // Refetch to update the photo list
    apolloClient.refetchQueries({ include: ['GetPhotos'] });
  };

  if (!user) {
    return null;
  }

  return (
    <div className={`flex flex-col h-full ${className}`}>
      <FileDropZone onFilesDropped={addFiles} onInvalidFiles={handleInvalidFiles} className="flex flex-col h-full">
        <PageHeader>
          <PageHeaderLabel
            title="Photos"
            icon={<PhotoIcon size={32} />}
          />
          <PageHeaderCommandWell>
            {/* Selection Controls Bar */}
            <SelectionControls
              selectedCount={selectedIds.length}
              totalCount={photos?.length || 0}
              onSelectAll={handleSelectAll}
              onClearSelection={handleClearSelection}
              onDeleteSelected={handleDeleteSelected}
              isDeleting={isDeleting}
              variant="inline"
              itemLabel="photo"
            />

            <PhotoUploadButton
              onBatchFilesSelected={addFiles}
              buttonText="Upload Photos"
              variant="primary"
              size="md"
              directMultiSelect={true}
            />
          </PageHeaderCommandWell>
        </PageHeader>

        <PhotoCollection2
          className="flex-1"
          photos={photosData}
          layout="magazine"
          itemTemplate="photo-only"
          zoom={1.0}
          selectionMode="multiple"
          selectedIds={selectedIds}
          onSelectionChange={handleSelectionChange}
          scrollFooter={
            <>
              {/* Infinite scroll sentinel - must be inside PhotoCollection2's scroll container */}
              <div
                ref={sentinelRef}
                className="h-4"
                role="status"
                aria-live="polite"
                aria-label={loading && hasMore ? 'Loading more photos' : undefined}
              />
              {loading && hasMore && (
                <div className="flex justify-center py-4">
                  <div className="text-neutral-400">Loading more photos...</div>
                </div>
              )}
            </>
          }
        />
      </FileDropZone>

      {/* Batch upload progress UI */}
      <BatchUploadProgress
        batchState={batchState}
        onRetry={retryUpload}
        onRemove={removeFile}
        onClearCompleted={clearCompleted}
      />
    </div>
  );
}
